/**
 * Teams Configuration Dialog Component
 *
 * Modal dialog for managing Microsoft Teams webhook URL configuration.
 */

import { ReactNode, useState, useEffect } from "react";
import { CheckCircle2, AlertCircle, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateOrganization } from "@/hooks/useOrganization";
import { useOrganizationContext } from "@/providers/OrganizationProvider";

interface TeamsConfigDialogProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function TeamsConfigDialog({ children, open, onOpenChange }: TeamsConfigDialogProps) {
  const { organization, refetchOrganization } = useOrganizationContext();
  const updateOrgMutation = useUpdateOrganization();
  const [webhookUrl, setWebhookUrl] = useState("");

  // Reset to empty when dialog opens (we only have last 4 chars, not the full URL)
  useEffect(() => {
    if (open) {
      setWebhookUrl("");
      updateOrgMutation.reset();
    }
  }, [open]);

  const handleReset = () => {
    setWebhookUrl("");
    updateOrgMutation.reset();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organization) return;

    try {
      await updateOrgMutation.mutateAsync({
        orgId: organization.id,
        data: { teams_webhook_url: webhookUrl.trim() || null },
      });
      refetchOrganization();
      // Close dialog after successful update
      setTimeout(() => {
        onOpenChange?.(false);
      }, 1500);
    } catch (error) {
      // Error will be shown via updateOrgMutation.error
    }
  };

  const isFormValid = webhookUrl.trim() === "" || webhookUrl.trim().startsWith("https://");
  const hasChanges = webhookUrl.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Microsoft Teams Integration</DialogTitle>
          <DialogDescription>
            Configure your Microsoft Teams webhook URL to receive alert notifications in Teams
            channels
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Configuration Status */}
          {organization?.teams_webhook_url_last_four && (
            <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Teams webhook is configured</p>
                <p className="text-xs mt-1 font-mono break-all">
                  ****{organization.teams_webhook_url_last_four}
                </p>
              </div>
            </div>
          )}

          {/* Success Alert */}
          {updateOrgMutation.isSuccess && (
            <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Webhook URL updated successfully!</p>
              </div>
            </div>
          )}

          {/* Error Alert */}
          {updateOrgMutation.isError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Failed to update webhook URL</p>
                <p className="text-xs mt-1">
                  {updateOrgMutation.error?.message || "Unable to update webhook URL"}
                </p>
              </div>
            </div>
          )}

          {/* Configuration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="teams-webhook-url">
                Teams Webhook URL{" "}
                <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="teams-webhook-url"
                type="url"
                placeholder="https://your-domain.webhook.office.com/webhookb2/..."
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                autoComplete="off"
              />
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">
                  Enter your Power Automate Workflows webhook URL. Leave empty to disable Teams
                  notifications.
                </p>
                <a
                  href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Learn how to create a Teams webhook
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={updateOrgMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isFormValid || !hasChanges || updateOrgMutation.isPending}
              >
                {updateOrgMutation.isPending ? "Saving..." : "Save Webhook URL"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
